import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import i18n from '../i18n';
import { localizePath } from '../utils/localePaths';
import { loadContentEnBodies } from '../content/contentEnBodiesLoader';

const LocaleContext = createContext({
  locale: 'fr',
  localePrefix: '',
});

export function LocaleLayout({ locale }) {
  const localePrefix = locale === 'en' ? '/en' : '';
  const [enContentReady, setEnContentReady] = useState(locale !== 'en');

  const value = useMemo(() => ({ locale, localePrefix }), [locale, localePrefix]);

  useEffect(() => {
    i18n.changeLanguage(locale);
    document.documentElement.lang = locale === 'en' ? 'en' : 'fr';
    try {
      window.localStorage.setItem('i18nextLng', locale);
    } catch {
      /* ignore */
    }
  }, [locale]);

  useEffect(() => {
    if (locale !== 'en') {
      setEnContentReady(true);
      return undefined;
    }

    let cancelled = false;
    setEnContentReady(false);

    loadContentEnBodies()
      .then(() => {
        if (!cancelled) {
          setEnContentReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEnContentReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  if (!enContentReady) {
    return null;
  }

  return (
    <LocaleContext.Provider value={value}>
      <Outlet />
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

/** Returns a function that prefixes paths with /en when locale is English */
export function useLocalizedPath() {
  const { localePrefix } = useLocale();
  return useMemo(() => (path) => localizePath(path, localePrefix), [localePrefix]);
}
